import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Search, Server, Activity, Target, Play, CheckCircle, XCircle, Clock, Zap, Database, MapPin, Shield, AlertTriangle, Wifi, Eye } from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import LicenseBlock from '../../components/LicenseBlock';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { toast } from '../../utils/toastUtils';

const SubdomainDiscovery = () => {
  usePageTitle('Découverte de Sous-domaines - SYLINK');
  const navigate = useNavigate();
  const { hasFeature, currentPlan, isFreePlan } = usePlanRestrictions();
  const { activeWorkspace } = useWorkspace();

  const [domain, setDomain] = useState('');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedSubdomains, setSelectedSubdomains] = useState([]);
  const [attackInProgress, setAttackInProgress] = useState(false);
  const [savedToWorkspace, setSavedToWorkspace] = useState(false);
  const [options, setOptions] = useState({
    use_subfinder: true,
    use_amass: false,
    use_dnsrecon: true,
    use_c99: true,
    use_greynoise: true,
    resolve_ips: true
  });
  const [expandedRow, setExpandedRow] = useState(null);

  // Load saved subdomain data from workspace on mount
  useEffect(() => {
    if (!activeWorkspace?.id) return;
    // Reset immédiatement au changement de workspace
    setResults(null);
    setDomain('');
    setSavedToWorkspace(false);
    const backendUrl = process.env.REACT_APP_API_URL || '';
    const token = localStorage.getItem('access_token');
    fetch(`${backendUrl}/api/v1/subdomains/workspace/${activeWorkspace.id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.status === 'success' && data.subdomains?.length > 0) {
          setResults({
            total_found: data.total_found || 0,
            alive_count: data.alive_count || 0,
            alive_subdomains: data.subdomains || [],
            tool_results: data.tool_results || {},
            duration_seconds: null,
            _fromCache: true,
            _domain: data.domain
          });
          setSavedToWorkspace(true);
          if (data.domain) setDomain(data.domain);
        }
      })
      .catch(() => {});
  }, [activeWorkspace?.id]);

  const handleDiscovery = async () => {
    if (!domain.trim()) {
      toast.warning('Veuillez entrer un domaine');
      return;
    }

    setScanning(true);
    setResults(null);
    setSavedToWorkspace(false);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${backendUrl}/api/v1/subdomains/discover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          domain: domain,
          ...options,
          auto_enrich: true,
          workspace_id: activeWorkspace?.id || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
        setSavedToWorkspace(data.saved_to_workspace || false);
        const saved = data.saved_to_workspace ? ' — sauvegardé dans le workspace' : '';
        toast.success(`${data.total_found || 0} sous-domaines découverts${saved}`);
      } else if (response.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Erreur lors de la découverte des sous-domaines');
      }
    } catch (error) {
      console.error('Discovery error:', error);
      toast.error('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      setScanning(false);
    }
  };

  const handleQuickDiscovery = async () => {
    if (!domain.trim()) {
      toast.warning('Veuillez entrer un domaine');
      return;
    }

    setScanning(true);
    setResults(null);
    setSavedToWorkspace(false);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('access_token');

      const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];

      const response = await fetch(`${backendUrl}/api/v1/subdomains/quick/${cleanDomain}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
        toast.success(`${data.total_found || 0} sous-domaines découverts`);
      } else if (response.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || 'Erreur lors de la découverte rapide');
      }
    } catch (error) {
      console.error('Quick discovery error:', error);
      toast.error('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      setScanning(false);
    }
  };

  const toggleSubdomain = (subdomain) => {
    setSelectedSubdomains(prev => {
      if (prev.includes(subdomain)) {
        return prev.filter(s => s !== subdomain);
      }
      return [...prev, subdomain];
    });
  };

  const selectAllAlive = () => {
    if (results && results.alive_subdomains) {
      const aliveSubdomains = results.alive_subdomains.map(s => s.subdomain);
      setSelectedSubdomains(aliveSubdomains);
    }
  };

  const handleAttackSelected = async () => {
    if (selectedSubdomains.length === 0) {
      toast.warning('Sélectionnez au moins un sous-domaine');
      return;
    }

    setAttackInProgress(true);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('access_token');
      let successCount = 0;
      let authError = false;

      // Create a scan for each subdomain
      for (const subdomain of selectedSubdomains) {
        const response = await fetch(`${backendUrl}/api/v1/scan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            scan_type: 'quick',
            target: subdomain,
            config: {
              is_subdomain_scan: true,
              original_domain: domain
            }
          })
        });

        if (response.status === 401) {
          authError = true;
          break;
        }
        if (response.ok) {
          successCount++;
        }
      }

      if (authError) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        localStorage.removeItem('access_token');
        navigate('/login');
        return;
      }

      toast.success(`${successCount} scan${successCount > 1 ? 's' : ''} lancé${successCount > 1 ? 's' : ''} sur les sous-domaines sélectionnés`);
      setSelectedSubdomains([]);
    } catch (error) {
      console.error('Attack error:', error);
      toast.error('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      setAttackInProgress(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="page-container">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-10 h-10 text-cyan-400" />
            <h1 className="text-4xl font-bold text-gradient-green">Découverte de Sous-domaines</h1>
          </div>
          <p className="text-gray-400">Découvrez les sous-domaines et lancez des attaques automatiques</p>
        </div>

        {/* Search Box */}
        <div className="bg-gradient-to-r from-cyan-900/40 to-cyan-800/20 rounded-xl border-2 border-cyan-500/50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Search className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-bold text-white">Recherche de Sous-domaines</h2>
          </div>

          <div className="space-y-4">
            <div className="flex gap-3">
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleQuickDiscovery()}
                placeholder="exemple.com"
                className="flex-1 px-4 py-3 bg-brand-black border-2 border-cyan-500/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
              <LicenseBlock feature="la découverte de sous-domaines">
              <PlanRestrictedButton
                feature="scan_basic"
                quotaType="scans_per_month"
                onClick={handleQuickDiscovery}
                disabled={scanning}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
                disabledClassName="!bg-gray-700 !from-gray-700 !to-gray-700 !text-gray-400"
                tooltipPosition="bottom"
              >
                {scanning ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Scan...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Scan rapide
                  </>
                )}
              </PlanRestrictedButton>
              </LicenseBlock>
              <PlanRestrictedButton
                feature="scan_advanced"
                quotaType="scans_per_month"
                onClick={handleDiscovery}
                disabled={scanning}
                className="px-6 py-3 bg-gradient-to-r from-brand-green to-green-600 text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-green/30 transition-all disabled:opacity-50 flex items-center gap-2"
                disabledClassName="!bg-gray-700 !from-gray-700 !to-gray-700 !text-gray-400"
                tooltipPosition="bottom"
              >
                {scanning ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full"></div>
                    Scan...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Scan complet
                  </>
                )}
              </PlanRestrictedButton>
            </div>

            {/* Options */}
            <div className="p-4 bg-brand-black/50 rounded-lg space-y-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Outils utilisés par le Scan complet</p>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer" title="Subfinder - Recherche passive rapide de sous-domaines">
                  <input
                    type="checkbox"
                    checked={options.use_subfinder}
                    onChange={(e) => setOptions({ ...options, use_subfinder: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span>Subfinder <span className="text-gray-500 text-xs">(passif)</span></span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer" title="Amass - Recherche approfondie de sous-domaines (plus lent)">
                  <input
                    type="checkbox"
                    checked={options.use_amass}
                    onChange={(e) => setOptions({ ...options, use_amass: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span>Amass <span className="text-gray-500 text-xs">(approfondi)</span></span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer" title="DNSRecon - Analyse des enregistrements DNS">
                  <input
                    type="checkbox"
                    checked={options.use_dnsrecon}
                    onChange={(e) => setOptions({ ...options, use_dnsrecon: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span>DNSRecon <span className="text-gray-500 text-xs">(DNS)</span></span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer" title="Résoudre les adresses IP de chaque sous-domaine découvert">
                  <input
                    type="checkbox"
                    checked={options.resolve_ips}
                    onChange={(e) => setOptions({ ...options, resolve_ips: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span>Résolution IP <span className="text-gray-500 text-xs">(DNS lookup)</span></span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer" title="C99.nl SubdomainFinder + Enrichissement (GeoIP, ASN, WAF, réputation IP, TOR, DNS complet)">
                  <input
                    type="checkbox"
                    checked={options.use_c99}
                    onChange={(e) => setOptions({ ...options, use_c99: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span>C99.nl <span className="text-gray-500 text-xs">(API — WAF, ASN, IP rep.)</span></span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer" title="GreyNoise — classification des IPs (malicious, noise, riot) sans clé API">
                  <input
                    type="checkbox"
                    checked={options.use_greynoise}
                    onChange={(e) => setOptions({ ...options, use_greynoise: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span>GreyNoise <span className="text-gray-500 text-xs">(threat intel)</span></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Cache / Saved notice */}
            {results._fromCache && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-900/30 border border-blue-500/30 rounded-lg text-blue-300 text-sm">
                <Database className="w-4 h-4" />
                Données chargées depuis le workspace (<span className="font-mono">{results._domain}</span>) — Relancez un scan pour actualiser
              </div>
            )}
            {savedToWorkspace && !results._fromCache && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-900/30 border border-green-500/30 rounded-lg text-green-300 text-sm">
                <Database className="w-4 h-4" />
                Résultats enrichis et sauvegardés dans le workspace
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-brand-black-light rounded-xl p-6 border border-cyan-500/30">
                <div className="flex items-center justify-between mb-2">
                  <Globe className="w-8 h-8 text-cyan-400" />
                  <span className="text-3xl font-bold text-cyan-400">{results.total_found || 0}</span>
                </div>
                <p className="text-gray-400 text-sm">Total Découverts</p>
              </div>

              <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
                <div className="flex items-center justify-between mb-2">
                  <Activity className="w-8 h-8 text-brand-green" />
                  <span className="text-3xl font-bold text-brand-green">{results.alive_count || 0}</span>
                </div>
                <p className="text-gray-400 text-sm">Actifs (Résolvables)</p>
              </div>

              <div className="bg-brand-black-light rounded-xl p-6 border border-purple-500/30">
                <div className="flex items-center justify-between mb-2">
                  <Target className="w-8 h-8 text-purple-400" />
                  <span className="text-3xl font-bold text-purple-400">{selectedSubdomains.length}</span>
                </div>
                <p className="text-gray-400 text-sm">Sélectionnés</p>
              </div>

              <div className="bg-brand-black-light rounded-xl p-6 border border-yellow-500/30">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-8 h-8 text-yellow-400" />
                  <span className="text-3xl font-bold text-yellow-400">{results.duration_seconds?.toFixed(1) || 0}s</span>
                </div>
                <p className="text-gray-400 text-sm">Durée</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={selectAllAlive}
                className="px-4 py-2 bg-brand-green text-black font-medium border border-brand-green rounded-lg hover:bg-brand-green/90 transition-all"
              >
                Sélectionner tous les actifs
              </button>
              <button
                onClick={() => setSelectedSubdomains([])}
                className="px-4 py-2 bg-gray-700 text-white font-medium border border-gray-600 rounded-lg hover:bg-gray-600 transition-all"
              >
                Désélectionner tout
              </button>
              <button
                onClick={handleAttackSelected}
                disabled={selectedSubdomains.length === 0 || attackInProgress}
                className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-red-500/30 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {attackInProgress ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Attaque...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Scanner les sélectionnés ({selectedSubdomains.length})
                  </>
                )}
              </button>
            </div>

            {/* Subdomains Table */}
            <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Server className="w-6 h-6 text-cyan-400" />
                Sous-domaines découverts
              </h3>

              {results.alive_subdomains && results.alive_subdomains.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-3 text-gray-400 font-medium w-10"></th>
                        <th className="text-left py-3 px-3 text-gray-400 font-medium">Sous-domaine</th>
                        <th className="text-left py-3 px-3 text-gray-400 font-medium">IP</th>
                        <th className="text-left py-3 px-3 text-gray-400 font-medium">Localisation</th>
                        <th className="text-left py-3 px-3 text-gray-400 font-medium">ASN / ISP</th>
                        <th className="text-left py-3 px-3 text-gray-400 font-medium">Ports / HTTP</th>
                        <th className="text-center py-3 px-3 text-gray-400 font-medium">WAF</th>
                        <th className="text-center py-3 px-3 text-gray-400 font-medium">Menaces</th>
                        <th className="text-center py-3 px-3 text-gray-400 font-medium w-10">DNS</th>
                        <th className="text-center py-3 px-3 text-gray-400 font-medium w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.alive_subdomains.map((sub, index) => (
                        <React.Fragment key={index}>
                          <tr
                            className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors cursor-pointer ${
                              selectedSubdomains.includes(sub.subdomain) ? 'bg-cyan-900/20' : ''
                            }`}
                            onClick={() => toggleSubdomain(sub.subdomain)}
                          >
                            {/* Checkbox */}
                            <td className="py-3 px-3">
                              <input
                                type="checkbox"
                                checked={selectedSubdomains.includes(sub.subdomain)}
                                onChange={() => toggleSubdomain(sub.subdomain)}
                                className="w-4 h-4 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                                onClick={e => e.stopPropagation()}
                              />
                            </td>

                            {/* Subdomain + CDN badge */}
                            <td className="py-3 px-3">
                              <span className="text-white font-mono text-xs">{sub.subdomain}</span>
                              {sub.cdn && (
                                <span className="ml-1.5 text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">{sub.cdn}</span>
                              )}
                            </td>

                            {/* IP */}
                            <td className="py-3 px-3">
                              <span className="text-cyan-400 font-mono text-xs">{sub.ip || '-'}</span>
                            </td>

                            {/* Localisation: country + city */}
                            <td className="py-3 px-3">
                              {sub.country ? (
                                <span className="flex items-center gap-1 text-gray-300 text-xs">
                                  <MapPin className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                  <span>{sub.country}{sub.city ? `, ${sub.city}` : ''}</span>
                                </span>
                              ) : <span className="text-gray-600">-</span>}
                            </td>

                            {/* ASN / ISP */}
                            <td className="py-3 px-3">
                              <div className="flex flex-col gap-0.5">
                                {sub.asn && (
                                  <span className="text-purple-400 font-mono text-xs">{sub.asn}</span>
                                )}
                                {sub.isp && (
                                  <span className="text-gray-400 text-xs truncate max-w-[120px]" title={sub.isp}>{sub.isp}</span>
                                )}
                                {!sub.asn && !sub.isp && <span className="text-gray-600">-</span>}
                              </div>
                            </td>

                            {/* Ports + HTTP status */}
                            <td className="py-3 px-3">
                              <div className="flex flex-wrap gap-1">
                                {sub.open_ports && sub.open_ports.length > 0 && sub.open_ports.map(p => (
                                  <span key={p} className="text-xs px-1 py-0.5 bg-cyan-900/40 text-cyan-400 rounded font-mono">{p}</span>
                                ))}
                                {sub.https_status && (
                                  <span className={`text-xs font-mono px-1 py-0.5 rounded ${sub.https_status < 400 ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
                                    {sub.https_status}🔒
                                  </span>
                                )}
                                {sub.http_status && (
                                  <span className={`text-xs font-mono px-1 py-0.5 rounded ${sub.http_status < 400 ? 'bg-yellow-900/40 text-yellow-400' : 'bg-red-900/40 text-red-400'}`}>
                                    {sub.http_status}
                                  </span>
                                )}
                                {(!sub.open_ports || sub.open_ports.length === 0) && !sub.https_status && !sub.http_status && (
                                  <span className="text-gray-600 text-xs">-</span>
                                )}
                              </div>
                            </td>

                            {/* WAF */}
                            <td className="py-3 px-3 text-center">
                              {sub.waf ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded border border-yellow-500/30">
                                  <Shield className="w-3 h-3" />
                                  {sub.waf}
                                </span>
                              ) : <span className="text-gray-600 text-xs">-</span>}
                            </td>

                            {/* Menaces: IP rep + TOR + Reverse IP count */}
                            <td className="py-3 px-3 text-center">
                              <div className="flex items-center justify-center gap-1 flex-wrap">
                                {sub.ip_reputation?.malicious ? (
                                  <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded border border-red-500/30" title={`Sur ${sub.ip_reputation.detections} blacklists`}>
                                    <AlertTriangle className="w-3 h-3" />
                                    {sub.ip_reputation.detections}BL
                                  </span>
                                ) : sub.ip_reputation ? (
                                  <span className="text-xs text-green-500" title="IP propre">✓</span>
                                ) : null}
                                {sub.is_tor && (
                                  <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded border border-purple-500/30" title="Nœud de sortie TOR">
                                    <Wifi className="w-3 h-3" />
                                    TOR
                                  </span>
                                )}
                                {sub.greynoise?.is_botnet && (
                                  <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded border border-red-500/30" title={`GreyNoise: ${sub.greynoise.name || 'malicious'}`}>
                                    <AlertTriangle className="w-3 h-3" />
                                    GN
                                  </span>
                                )}
                                {sub.greynoise && !sub.greynoise.is_botnet && sub.greynoise.noise && (
                                  <span className="text-xs text-orange-400" title="GreyNoise: internet scanner">scanner</span>
                                )}
                                {sub.reverse_ip_count != null && (
                                  <span className="text-xs text-gray-500" title={`${sub.reverse_ip_count} domaines sur la même IP`}>
                                    {sub.reverse_ip_count}dom.
                                  </span>
                                )}
                                {!sub.ip_reputation && !sub.is_tor && !sub.greynoise && sub.reverse_ip_count == null && (
                                  <span className="text-gray-600 text-xs">-</span>
                                )}
                              </div>
                            </td>

                            {/* DNS expand button */}
                            <td className="py-3 px-3 text-center">
                              {sub.dns_records ? (
                                <button
                                  onClick={e => { e.stopPropagation(); setExpandedRow(expandedRow === index ? null : index); }}
                                  className={`p-1 rounded transition-colors ${expandedRow === index ? 'text-cyan-400 bg-cyan-500/20' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-700'}`}
                                  title="Voir les enregistrements DNS"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              ) : <span className="text-gray-700 text-xs">-</span>}
                            </td>

                            {/* Status */}
                            <td className="py-3 px-3 text-center">
                              {sub.alive || sub.ip ? (
                                <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <XCircle className="w-4 h-4 text-gray-600 mx-auto" />
                              )}
                            </td>
                          </tr>

                          {/* DNS Records expanded row */}
                          {expandedRow === index && sub.dns_records && (
                            <tr className="border-b border-gray-800 bg-gray-900/50">
                              <td colSpan={10} className="px-6 py-4">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
                                  {sub.dns_records.a?.length > 0 && (
                                    <div>
                                      <span className="text-cyan-400 font-semibold uppercase tracking-wider">A</span>
                                      <div className="mt-1 space-y-0.5">
                                        {sub.dns_records.a.map((r, i) => <div key={i} className="font-mono text-gray-300">{r}</div>)}
                                      </div>
                                    </div>
                                  )}
                                  {sub.dns_records.aaaa?.length > 0 && (
                                    <div>
                                      <span className="text-cyan-400 font-semibold uppercase tracking-wider">AAAA</span>
                                      <div className="mt-1 space-y-0.5">
                                        {sub.dns_records.aaaa.map((r, i) => <div key={i} className="font-mono text-gray-300 break-all">{r}</div>)}
                                      </div>
                                    </div>
                                  )}
                                  {sub.dns_records.mx?.length > 0 && (
                                    <div>
                                      <span className="text-yellow-400 font-semibold uppercase tracking-wider">MX</span>
                                      <div className="mt-1 space-y-0.5">
                                        {sub.dns_records.mx.map((r, i) => <div key={i} className="font-mono text-gray-300">{r}</div>)}
                                      </div>
                                    </div>
                                  )}
                                  {sub.dns_records.ns?.length > 0 && (
                                    <div>
                                      <span className="text-purple-400 font-semibold uppercase tracking-wider">NS</span>
                                      <div className="mt-1 space-y-0.5">
                                        {sub.dns_records.ns.map((r, i) => <div key={i} className="font-mono text-gray-300">{r}</div>)}
                                      </div>
                                    </div>
                                  )}
                                  {sub.dns_records.txt?.length > 0 && (
                                    <div className="col-span-2">
                                      <span className="text-green-400 font-semibold uppercase tracking-wider">TXT</span>
                                      <div className="mt-1 space-y-0.5">
                                        {sub.dns_records.txt.map((r, i) => <div key={i} className="font-mono text-gray-400 break-all text-[10px]">{r}</div>)}
                                      </div>
                                    </div>
                                  )}
                                  {sub.dns_records.cname?.length > 0 && (
                                    <div>
                                      <span className="text-orange-400 font-semibold uppercase tracking-wider">CNAME</span>
                                      <div className="mt-1 space-y-0.5">
                                        {sub.dns_records.cname.map((r, i) => <div key={i} className="font-mono text-gray-300">{r}</div>)}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-10">
                  <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun sous-domaine alive trouvé</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Ce domaine n'a peut-être pas de sous-domaines publics indexés
                  </p>
                </div>
              )}
            </div>

            {/* Tool Results */}
            {results.tool_results && (
              <div className="bg-brand-black-light rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-white mb-4">Résultats par outil</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(results.tool_results).map(([tool, data]) => (
                    <div key={tool} className="p-4 bg-brand-black rounded-lg border border-gray-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-cyan-400 font-semibold capitalize">{tool}</span>
                        <span className={`text-lg font-bold ${data.count > 0 ? 'text-brand-green' : 'text-gray-500'}`}>
                          {data.count || 0}
                        </span>
                      </div>
                      {data.error && (
                        <p className="text-red-400 text-xs">{data.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!results && !scanning && (
          <div className="bg-brand-black-light rounded-xl p-12 border border-brand-green/30 text-center">
            <Globe className="w-24 h-24 text-cyan-400/50 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-gray-300 mb-4">Découverte de Sous-domaines</h3>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Entrez un domaine ci-dessus pour découvrir tous ses sous-domaines.
              <br/>
              Utilisez <strong className="text-cyan-400">Scan rapide</strong> pour une recherche rapide avec Subfinder,
              ou <strong className="text-brand-green">Scan complet</strong> pour une recherche approfondie.
            </p>
            <div className="mt-8 p-4 bg-cyan-900/20 border border-cyan-500/30 rounded-lg max-w-xl mx-auto">
              <p className="text-cyan-400 text-sm">
                <strong>Outils configurables :</strong> Subfinder, Amass, DNSRecon, Résolution IP
              </p>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default SubdomainDiscovery;
